import cn from 'classnames';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon, IconButton, Tooltip } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Toggle } from 'components/form';
import Modal from 'components/Modal';

import { useEvent } from 'hooks/useEvent';
import { EventTouchpointable } from 'lib/models/event';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';
import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './RegistrationSettingModal.module.scss';

interface RegistrationSettingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RegistrationSettingModal = ({
  isOpen,
  onClose,
}: RegistrationSettingModalProps): ReactElement => {
  const router = useRouter();
  const { id } = router.query;
  const { eventSettings } = useEvent();
  const { touchpoint, touchpointDashboard } = useAttendeesFilter();

  const [t] = useTranslation('event-dashboard');
  const { touchpointable, show_guest_list, creatable_for } = touchpoint || {};
  const {
    enforce_waitlist_for_all,
    enforce_capacity,
    capacity,
    waitlist,
    id: touchpointableId,
  } = parseResponse(touchpointable as EventTouchpointable) || {};

  const { waitlist_count } = touchpointDashboard || {};

  const isSocietyEvent = useMemo(() => {
    return creatable_for?.data.type === 'student_society';
  }, [creatable_for]);

  const editLink = useMemo(() => {
    return `${(isSocietyEvent
      ? PAGE_ROUTES.EVENTS_EDIT
      : PAGE_ROUTES.CAMPAIGNS_EDIT
    ).replace('[id]', id as string)}?step=1&type=${TouchpointType.Event}`;
  }, [id, isSocietyEvent]);

  const [capacityError, setCapacityError] = useState<boolean>(false);

  const [eventCapacity, setEventCapacity] = useState<number>(0);
  const [enforceCapacity, setEnforceCapacity] = useState(false);
  const [enforceWaitlistForAll, setEnforceWaitlistForAll] = useState(false);
  const [showGuestList, setShowGuestList] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);

  useEffect(() => {
    if (touchpointable) {
      setEventCapacity(capacity);
      setEnforceCapacity(enforce_capacity);
      setEnforceWaitlistForAll(enforce_waitlist_for_all);
      setShowGuestList(show_guest_list || false);
      setWaitlistEnabled(waitlist);
    }
  }, [
    touchpointable,
    enforce_waitlist_for_all,
    enforce_capacity,
    capacity,
    show_guest_list,
    waitlist,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentClassName={styles.container}
    >
      <IconButton
        size="small"
        iconName="close"
        className={styles.cancelIcon}
        onClick={() => onClose()}
      />
      <div className={styles.body}>
        <div className={styles.title}>{t('registration-settings.title')}</div>
        <div className={styles.content}>
          <div className={styles.row}>
            <span className={styles.fieldName}>
              {t('registration-settings.require-approval')}
            </span>
            <Toggle
              id="require-approval"
              checked={enforceWaitlistForAll}
              onChange={() => {
                setEnforceWaitlistForAll((flag) => {
                  if (!flag) {
                    setEnforceCapacity(false);
                  }
                  return !flag;
                });
              }}
            />
          </div>
          {enforce_waitlist_for_all === true &&
          waitlist_count &&
          enforceWaitlistForAll === false ? (
            <div className={styles.warning}>
              <Icon iconName="icon_info" size="large" />
              <span>{t('registration-settings.warning-disable-approval')}</span>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.row}>
            <span className={styles.fieldName}>
              {t('registration-settings.enforce-capacity')}
            </span>
            <Toggle
              id="event-capacity"
              checked={enforceCapacity}
              onChange={() => {
                setEnforceCapacity((flag) => {
                  if (!flag) {
                    setEnforceWaitlistForAll(false);
                  }
                  return !flag;
                });
              }}
            />
          </div>
          {enforce_capacity === true &&
          enforceCapacity === false &&
          enforceWaitlistForAll === false ? (
            <div className={styles.warning}>
              <Icon
                className={styles.icon}
                iconName="icon_info"
                size="xmediumplus"
              />
              <span>{t('registration-settings.warning-disable-capacity')}</span>
            </div>
          ) : (
            <></>
          )}
          {enforceCapacity && (
            <>
              <div className={styles.rowInput}>
                <span className={styles.fieldName}>
                  {t('registration-settings.max-capacity')}
                </span>
                <InputV2
                  className={styles.input}
                  type="number"
                  placeholder={t(
                    'registration-settings.max-capacity-placeholder'
                  )}
                  value={eventCapacity}
                  onChange={(e) => {
                    setEventCapacity(parseInt(e.target.value));
                    setCapacityError(false);
                  }}
                  isInvalid={capacityError}
                  onWheel={(e) => {
                    const target = e.target as HTMLElement;
                    target.blur();
                  }}
                />
              </div>
              {capacity && eventCapacity > capacity && (
                <div className={styles.warning}>
                  <Icon iconName="icon_info" size="large" />
                  <span>
                    {t('registration-settings.warning-increase-capacity')}
                  </span>
                </div>
              )}
            </>
          )}
          <div className={styles.row}>
            {t('registration-settings.enable-waitlist')}
            <span className={styles.fieldName}></span>
            <Tooltip
              content={t(
                eventCapacity && enforceCapacity
                  ? 'registration-settings.tooltip-no-capacity'
                  : 'registration-settings.tooltip-capacity'
              )}
              position="top"
              xAlign="right"
              color="dark"
              className={cn(styles.tooltip, styles.waitlist, {
                [styles.big]: enforceCapacity && eventCapacity,
              })}
            >
              <Toggle
                id="enable-waitlist"
                checked={waitlistEnabled}
                onChange={() => {
                  setWaitlistEnabled((flag) => !flag);
                }}
                disabled={!eventCapacity || !enforceCapacity}
              />
            </Tooltip>
          </div>
          <div className={styles.row}>
            <span className={styles.fieldName}>
              {t('registration-settings.show-guest-list')}
            </span>
            <Tooltip
              content={t('registration-settings.tooltip-show-guest')}
              position="top"
              xAlign="right"
              className={styles.tooltip}
              color="dark"
            >
              <Toggle
                id="show-guest-list"
                checked={showGuestList}
                onChange={() => {
                  setShowGuestList((flag) => !flag);
                }}
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.separator}></div>
        <div className={styles.section}>
          <ButtonV3
            onClick={() => {
              router.push(editLink);
            }}
            color="secondary"
          >
            {t('registration-settings.edit-event')}
          </ButtonV3>

          <ButtonV3
            onClick={async () => {
              if (
                enforceCapacity &&
                (eventCapacity === null || eventCapacity <= 0)
              ) {
                setCapacityError(true);
                return;
              }

              const data = {
                enforceCapacity: enforceCapacity,
                capacity: enforceCapacity ? eventCapacity : null,
                enableWaitlist: waitlistEnabled,
                enableForceWaitlist: enforceWaitlistForAll,
                showGuestList,
                id: touchpointableId,
              };

              await eventSettings({
                data,
                id: id as string,
              });
              onClose();
            }}
          >
            {t('registration-settings.done')}
          </ButtonV3>
        </div>
      </div>
    </Modal>
  );
};
